import React, { useContext, useEffect, useMemo, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { useTranslation } from 'react-i18next';
import { IItemForGetShortInfo, UserRoles, IGetCustomersShort, EntityStatus } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { Rule } from 'antd/lib/form';
import { ISelectOption } from 'components/Select/types';
import { getCustomersShortInfo } from 'backend/customers';
import FormSelect from 'components/Select/FormSelect';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

interface ICustomerSelect {
	setIDCustomer: (value: Nullable<string>) => void;
	setFetchedCustomers: (value: IItemForGetShortInfo[]) => void;
	initialIDSelectedCustomer: Nullable<string>;
}

const CustomerSelect: React.FC<ICustomerSelect> = ({
	setIDCustomer,
	setFetchedCustomers,
	initialIDSelectedCustomer,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(
		hasAccess ? getCustomersShortInfo : null,
	);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectCustomer') }], [t]);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		const responseData = response ? response.data.customers : [];
		const loggedUserData = loggedUser.customers || [];
		const items = hasAccess ? responseData : loggedUserData;

		return items.map((customer) => ({
			...customer,
			additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [hasAccess, response, loggedUser]);

	useEffect(() => {
		if (hasAccess && response && initialIDSelectedCustomer) {
			setFetchedCustomers(response.data.customers);
		}
		if (!hasAccess && initialIDSelectedCustomer) {
			const accessibleCustomers = loggedUser.customers || [];
			setFetchedCustomers(accessibleCustomers);
		}
		if (isFirstRender && (response || !hasAccess) && initialIDSelectedCustomer) {
			setIDCustomer(initialIDSelectedCustomer);
			setIsFirstRender(false);
		}
	}, [response, loggedUser, hasAccess, initialIDSelectedCustomer]);

	return (
		<FormSelect
			testId="CustomerSelect"
			name="IDSelectedCustomer"
			label="customerLabel"
			options={mappedOptions}
			fetchError={responseError}
			loading={loading}
			disabled={true}
			onRefetch={refetch}
			validationRules={validationRules}
		/>
	);
};

export default React.memo(CustomerSelect);
